/**
 * Import Monitor — Auto-Cancelled tab.
 *
 * The ERP full-snapshot reconcile retires schedule slots that drop out of the
 * feed, and a wrongly-scoped push once cancelled slots it shouldn't have.
 * Each cancellation is audited in the PO changelog, but only per-PO — this
 * tab lists them all in one place, newest first, with whether the slot has
 * since been reactivated (reappeared in a later push). Still-cancelled rows
 * are the ones worth a look.
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, Search, Undo2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  importsAPI,
  type AutoCancelledResponse,
} from '../../lib/api/imports';

interface ImportAutoCancelledTabProps {
  customerId: number;
}

const PAGE_SIZE = 25;

export default function ImportAutoCancelledTab({ customerId }: ImportAutoCancelledTabProps) {
  const [data, setData] = useState<AutoCancelledResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [onlyStillCancelled, setOnlyStillCancelled] = useState(false);
  // Spec: 24h / 48h / 7d / 14d windows, defaulting to 48 hours.
  const [days, setDays] = useState(2);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await importsAPI.getAutoCancelled(customerId, {
        days,
        search: search || undefined,
        onlyStillCancelled,
        page,
        pageSize: PAGE_SIZE,
      });
      setData(resp);
    } catch (err: unknown) {
      console.error('Auto-cancelled load failed:', err);
      setError('Failed to load auto-cancelled orders');
      toast.error('Failed to load auto-cancelled orders');
    } finally {
      setLoading(false);
    }
  }, [customerId, days, search, onlyStillCancelled, page]);

  useEffect(() => { load(); }, [load]);

  const totalPages = data ? Math.max(1, Math.ceil(data.count / PAGE_SIZE)) : 1;

  return (
    <div className="space-y-4">
      {/* Summary strip */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card><CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground">Auto-cancelled ({data.summary.window_days === 1 ? '24 hrs' : data.summary.window_days === 2 ? '48 hrs' : `${data.summary.window_days} days`})</p>
            <p className="text-2xl font-semibold tabular-nums">{data.summary.total.toLocaleString()}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground">Still cancelled</p>
            <p className="text-2xl font-semibold tabular-nums text-red-600">{data.summary.still_cancelled.toLocaleString()}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground">Reactivated since</p>
            <p className="text-2xl font-semibold tabular-nums text-emerald-600">{data.summary.reactivated.toLocaleString()}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground">Closed · receipts kept</p>
            <p className="text-2xl font-semibold tabular-nums text-amber-700">{data.summary.closed.toLocaleString()}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground">Last 24h</p>
            <p className="text-2xl font-semibold tabular-nums">{data.summary.last_24h.toLocaleString()}</p>
          </CardContent></Card>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <input
            className="pl-8 pr-3 py-2 text-sm border rounded-md w-64"
            placeholder="PO, PO/line or part…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { setSearch(searchInput.trim()); setPage(1); }
            }}
          />
        </div>
        <select
          className="text-sm border rounded-md py-2 px-2"
          value={days}
          onChange={(e) => { setDays(parseInt(e.target.value) || 2); setPage(1); }}
        >
          <option value={1}>Last 24 hours</option>
          <option value={2}>Last 48 hours</option>
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
        </select>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={onlyStillCancelled}
            onChange={(e) => { setOnlyStillCancelled(e.target.checked); setPage(1); }}
          />
          Still cancelled only
        </label>
        <Button variant="outline" size="sm" onClick={() => load()} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading && !data ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
            </div>
          ) : error ? (
            <div className="py-12 text-center text-sm text-red-600">{error}</div>
          ) : !data || data.results.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No auto-cancelled orders in this window — all clear.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/40 text-left">
                    {/* change_date = when OUR reconcile detected the slot missing
                        from the ERP snapshot and cancelled it. */}
                    <th className="py-2 px-3 font-medium">Detected</th>
                    <th className="py-2 px-3 font-medium">PO</th>
                    <th className="py-2 px-3 font-medium">Line</th>
                    <th className="py-2 px-3 font-medium">Sched</th>
                    <th className="py-2 px-3 font-medium">Sched Date</th>
                    <th className="py-2 px-3 font-medium">Part</th>
                    <th className="py-2 px-3 font-medium text-right">Qty</th>
                    <th className="py-2 px-3 font-medium">Was</th>
                    <th className="py-2 px-3 font-medium">Now</th>
                    <th className="py-2 px-3 font-medium">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {data.results.map((r, i) => (
                    <tr key={`${r.po_number}-${r.schedule_line_number}-${i}`} className="border-b last:border-0 hover:bg-muted/20">
                      <td className="py-2 px-3 whitespace-nowrap">{new Date(r.cancelled_at).toLocaleString()}</td>
                      <td className="py-2 px-3 font-medium">{r.po_number}</td>
                      <td className="py-2 px-3">{r.line_number ?? '—'}</td>
                      <td className="py-2 px-3">{r.schedule_line_number ?? '—'}</td>
                      <td className="py-2 px-3 whitespace-nowrap">{r.schedule_date ?? '—'}</td>
                      <td className="py-2 px-3">{r.part_number ?? '—'}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{r.schedule_qty ?? '—'}</td>
                      <td className="py-2 px-3">{r.previous_status}</td>
                      <td className="py-2 px-3">
                        {r.reactivated ? (
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                            <Undo2 className="h-3 w-3 mr-1" /> Reactivated
                          </Badge>
                        ) : r.outcome === 'closed' ? (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-200"
                            title="Receipts were linked to this slot, so it was closed with them kept rather than cancelled">
                            <XCircle className="h-3 w-3 mr-1" /> Closed · receipts kept
                          </Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800 border-red-200">
                            <XCircle className="h-3 w-3 mr-1" /> Cancelled
                          </Badge>
                        )}
                      </td>
                      <td className="py-2 px-3 text-xs text-muted-foreground max-w-[280px] truncate" title={r.reason}>{r.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {data && data.count > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Page {data.page} of {totalPages} · {data.count.toLocaleString()} cancellations
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
